import React, { useState, useEffect, useCallback } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Reservation } from "../types/types";
import { getPriceForDate, fetchPricingRules } from "../utils/pricing.ts";
import "../styles/BookingCalendar.css";

interface MinimumStayRule {
  id: number;
  startDate: string;
  endDate: string;
  minimumNights: number;
  message: string;
}

interface BookingCalendarProps {
  existingReservations: Reservation[];
  onDateSelection: (startDate: Date | null, endDate: Date | null) => void;
}

export const BookingCalendar: React.FC<BookingCalendarProps> = ({
  existingReservations,
  onDateSelection,
}) => {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [minimumStayRules, setMinimumStayRules] = useState<MinimumStayRule[]>(
    []
  );

  useEffect(() => {
    fetchPricingRules().catch((error) => {
      console.error("Error fetching pricing rules:", error);
    });

    fetchMinimumStayRules().catch((error) => {
      console.error("Error fetching minimum stay rules:", error);
    });
  }, []);

  const fetchMinimumStayRules = async () => {
    try {
      const response = await fetch(
        //"http://localhost:3331/api/minimum-stay-rules"
        "https://api.nameren-rai.com/api/minimum-stay-rules"
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setMinimumStayRules(data);
    } catch (error) {
      console.error("Error fetching minimum stay rules:", error);
    }
  };

  const scrollToBottom = () => {
    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: "smooth",
    });
  };

  const isPeriodAvailable = useCallback(
    (startDate: Date, endDate: Date) => {
      // Има само едно бунгало - проверяваме дали няма припокриващи се резервации
      const start = new Date(
        startDate.getFullYear(),
        startDate.getMonth(),
        startDate.getDate()
      );
      const end = new Date(
        endDate.getFullYear(),
        endDate.getMonth(),
        endDate.getDate()
      );

      const hasOverlap = existingReservations.some((reservation) => {
        const reservationStart = new Date(reservation.startDate);
        const reservationEnd = new Date(reservation.endDate);
        const resStart = new Date(
          reservationStart.getFullYear(),
          reservationStart.getMonth(),
          reservationStart.getDate()
        );
        const resEnd = new Date(
          reservationEnd.getFullYear(),
          reservationEnd.getMonth(),
          reservationEnd.getDate()
        );
        return start <= resEnd && end >= resStart;
      });

      return !hasOverlap;
    },
    [existingReservations]
  );

  const isDateDisabled = useCallback(
    (date: Date) => {
      // Ако няма избрана начална дата, проверяваме дали датата е заета от някоя резервация
      if (!startDate) {
        const checkDate = new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate()
        );

        const isBooked = existingReservations.some((reservation) => {
          const resStart = new Date(reservation.startDate);
          const resEnd = new Date(reservation.endDate);
          const rStart = new Date(
            resStart.getFullYear(),
            resStart.getMonth(),
            resStart.getDate()
          );
          const rEnd = new Date(
            resEnd.getFullYear(),
            resEnd.getMonth(),
            resEnd.getDate()
          );
          return checkDate >= rStart && checkDate <= rEnd;
        });

        return isBooked;
      }

      // Ако има избрана начална дата, проверяваме дали периодът е свободен
      return !isPeriodAvailable(startDate, date);
    },
    [startDate, existingReservations, isPeriodAvailable]
  );

  const validateMinimumStay = (start: Date, end: Date) => {
    // Calculate nights by counting the days between start and end (inclusive)
    const stayLength =
      Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    // Find applicable rule for the start date
    const applicableRule = minimumStayRules.find((rule) => {
      const ruleStart = new Date(rule.startDate);
      const ruleEnd = new Date(rule.endDate);
      return start >= ruleStart && start <= ruleEnd;
    });

    if (applicableRule && stayLength < applicableRule.minimumNights) {
      alert(applicableRule.message);
      return false;
    }
    return true;
  };

  const handleStartDateChange = (date: Date | null) => {
    setStartDate(date);
    onDateSelection(date, endDate);
  };

  const handleEndDateChange = (date: Date | null) => {
    if (startDate && date) {
      if (!validateMinimumStay(startDate, date)) {
        return;
      }

      if (!isPeriodAvailable(startDate, date)) {
        alert("Няма наличност за избрания период!");
        return;
      }

      setEndDate(date);
      onDateSelection(startDate, date);
    }
  };

  const renderDayContents = (day: number, date: Date) => (
    <div className="calendar-day">
      <div className="day-number ttx-number">{day}</div>
      <div className="day-price">{getPriceForDate(date)}€</div>
    </div>
  );

  return (
    <div className="booking-calendar">
      <div className="date-picker-container">
        <DatePicker
          selected={startDate}
          onChange={handleStartDateChange}
          selectsStart
          startDate={startDate}
          endDate={endDate}
          minDate={new Date()}
          filterDate={(date) => !isDateDisabled(date)}
          dateFormat="dd/MM/yyyy"
          placeholderText="Начална дата"
          renderDayContents={renderDayContents}
          onFocus={scrollToBottom}
        />
        <DatePicker
          selected={endDate}
          onChange={handleEndDateChange}
          selectsEnd
          startDate={startDate}
          endDate={endDate}
          minDate={startDate || new Date()}
          filterDate={(date) => !isDateDisabled(date)}
          dateFormat="dd/MM/yyyy"
          placeholderText="Крайна дата"
          renderDayContents={renderDayContents}
          onFocus={scrollToBottom}
        />
      </div>
    </div>
  );
};
